
import { BookOutlined, LinkOutlined } from '@ant-design/icons';
import {
  type Settings as LayoutSettings,
  type MenuDataItem,
  PageLoading,
  RouteContext,
} from '@ant-design/pro-layout';
import { ConfigProvider, notification } from 'antd';
import { AliveScope, fixContext } from 'react-activation';
import type { RequestConfig, RunTimeLayoutConfig } from 'umi';
import { history, Link } from 'umi';
import Footer from '@/components/Footer';
import RightContent from '@/components/RightContent';
import IconMap from './components/IconMap/IconMap';
import TabsBar from './components/Tabs';
import {
  getRouters,
  getLoginUser as queryCurrentUser,
} from './services/ant-design-pro/login';

fixContext(RouteContext);
fixContext(ConfigProvider.ConfigContext);

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

const redirectToLogin = () => {
  if (wxtoken === '') {
    localStorage.clear();
    history.push(loginPath);
  }
};

let wxtoken: string = '';
if (window.location.search !== '') {
  const tokendata: string[] = window.location.search.split('token=');
  if (tokendata.length > 1 && tokendata[1] !== '') {
    wxtoken = tokendata[1].toString();
    if (localStorage.getItem('token') === null) {
      localStorage.setItem('token', wxtoken);
    }
  }
}

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.User;
  menu?: any;
  permissions?: any;
  token?: string;
  fetchUserInfo?: () => Promise<API.UserInfo | undefined>;
  fetchMenuData?: () => Promise<any | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const response: any = await queryCurrentUser({});
      if (typeof response?.code !== 'undefined') {
        if (response.code !== 200) {
          redirectToLogin();
          return undefined;
        }
        return response.data as API.UserInfo;
      }
      return response as API.UserInfo;
    } catch (error) {
      redirectToLogin();
    }
    return undefined;
  };
  const fetchMenuData = async () => {
    try {
      const result: API.RouterVo[] = await getRouters({});
      localStorage.setItem('menu', JSON.stringify(result));
      return result;
    } catch (error) {
      return [];
    }
  };

  if (
    history.location.pathname !== loginPath &&
    localStorage.getItem('token') !== null
  ) {
    const userInfo = await fetchUserInfo();
    const currentUser = userInfo?.user;
    const permissions = userInfo?.permissions;
    const menu = await fetchMenuData();
    const token = localStorage.getItem('token') as string;
    return {
      fetchUserInfo,
      fetchMenuData,
      currentUser,
      permissions,
      menu,
      token,
      settings: {},
    };
  }

  return {
    fetchUserInfo,
    fetchMenuData,
    settings: {},
    menu: [],
  };
}

const authHeaderInterceptor = (url: string, options: any) => {
  let token = localStorage.getItem('token');
  if (wxtoken !== '' && token && token !== 'undefined') {
    token = wxtoken;
  }
  let authHeader = {};
  if (token && token !== 'undefined' && url !== '/system/login/login') {
    authHeader = { Authorization: `Bearer ${token}` };
  }
  // 如果 URL 已经是完整地址（http:// 或 https:// 开头），直接使用，不添加 /api 前缀
  const isFullUrl = url.startsWith('http://') || url.startsWith('https://');
  return {
    url: isFullUrl ? url : `/api${url}`,
    options: { ...options, interceptors: true, headers: authHeader },
  };
};

export const request = {
  errorHandler: (error: any) => {
    const { response, data } = error;
    const errorInfo = {
      400: '发出的请求有错误，服务器没有进行新建或修改数据的操作。',
      401: '用户没有权限（令牌、用户名、密码错误）。',
      403: '用户得到授权，但是访问是被禁止的。',
      404: '发出的请求针对的是不存在的记录，服务器没有进行操作。',
      405: '请求方法不被允许。',
      406: '请求的格式不可得。',
      410: '请求的资源被永久删除，且不会再得到的。',
      422: '当创建一个对象时，发生一个验证错误。',
      500: '服务器发生错误，请检查服务器。',
      502: '网关错误。',
      503: '服务不可用，服务器暂时过载或维护。',
      504: '网关超时。',
    };

    // 处理认证相关错误，跳转到登录页
    if (response) {
      const status = response.status;
      if (status === 401 || status === 403) {
        // 清除本地存储
        localStorage.clear();
        // 跳转到登录页
        if (history.location.pathname !== loginPath) {
          history.push(loginPath);
        }
        const statusKey = status as keyof typeof errorInfo;
        notification.error({
          description: errorInfo[statusKey] || '认证失败',
          message: '认证失败',
        });
        throw error;
      }
    }

    // 处理 AjaxResult 格式的响应错误
    if (data && typeof data === 'object' && 'code' in data && 'msg' in data) {
      const ajaxResult = data as API.AjaxResult;
      // 如果 code 不为 200 或 0，表示业务错误
      if (
        ajaxResult.code !== undefined &&
        ajaxResult.code !== 200 &&
        ajaxResult.code !== 0
      ) {
        notification.error({
          description: ajaxResult.msg || '操作失败',
          message: `错误码: ${ajaxResult.code}`,
        });
        throw error;
      }
    }

    if (!response) {
      notification.error({
        description: '您的网络发生异常，无法连接服务器',
        message: '网络异常',
      });
    } else if (response.status === 400) {
      notification.error({
        message: JSON.stringify(data),
      });
    } else {
      const statusKey = response.status as keyof typeof errorInfo;
      const errorMessage = errorInfo[statusKey] || '未知错误';
      notification.error({
        description: errorMessage,
        message: response.url,
      });
    }

    throw error;
  },
  requestInterceptors: [authHeaderInterceptor],
} as RequestConfig;

const loopMenuItem = (menuList: MenuDataItem[]): MenuDataItem[] =>
  menuList.map(({ icon, children, ...item }) => {
    return {
      ...item,
      icon: IconMap[icon as string],
      children: children ? loopMenuItem(children) : undefined,
    };
  });

export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  return {
    rightContentRender: () => <RightContent />,
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      const hasToken = localStorage.getItem('token');
      if (
        !initialState?.currentUser &&
        !hasToken &&
        location.pathname !== loginPath
      ) {
        if (wxtoken !== '') {
          localStorage.setItem('token', wxtoken);
          history.push(location.pathname);
        } else {
          localStorage.clear();
          history.push(loginPath);
        }
      }
    },
    links: isDev
      ? [
          // <Link to="/umi/plugin/openapi" target="_blank">
          //   <LinkOutlined />
          //   <span>OpenAPI 文档</span>
          // </Link>,
          // <Link to="/~docs">
          //   <BookOutlined />
          //   <span>业务组件文档</span>
          // </Link>,
        ]
      : [],
    menuDataRender: () => loopMenuItem(initialState?.menu),
    childrenRender: (children) => {
      return (
        <AliveScope>
          <TabsBar />
          {children}
        </AliveScope>
      );
    },
    ...initialState?.settings,
  };
};
